import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux';
import 'animate.css'
import {
	getOrganizations,
	deleteOrganization
} from '../../../firebaseActions.mjs';
import { storeAddOrganization, storeClearOrganizations, storeDeleteOrganization } from '../../../actions/OrganizationsActions'
import ConfirmDelete from '../../utils/ConfirmDeleteModal'
import AddOrganization from './AddOrganization.jsx';
import { Notification } from '../../utils/Misc.jsx';
import Organization from './Organization.jsx';

const MIN_ORGS_TO_SHOW_SEARCH = 10;

const Organizations = ({ user }) => {
	const dispatch = useDispatch()
	const { organizations } = useSelector(state => state.organizations);

	const [isLoading, setIsLoading] = useState(true);
	const [loadError, setLoadError] = useState(null);
	const [deleteTargetId, setDeleteTargetId] = useState(null); // null = closed, otherwise org id
	const [isDeleting, setIsDeleting] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const [currentOrganizationId, setCurrentOrganizationId] = useState(null);

	const sortedOrganizations = useMemo(() => {
		return [...organizations].sort((a, b) => a.data.name.localeCompare(b.data.name));
	}, [organizations]);

	const showOrgsSearch = organizations.length > MIN_ORGS_TO_SHOW_SEARCH;

	const filteredAndSortedOrganizations = useMemo(() => {
		if (!searchTerm) return sortedOrganizations;
		const term = searchTerm.toLowerCase();
		return sortedOrganizations.filter((org) => org.data.name.toLowerCase().includes(term));
	}, [sortedOrganizations, searchTerm]);

	const currentOrganization = useMemo(() => {
		if (!filteredAndSortedOrganizations.length) return null;
		if (currentOrganizationId) {
			const found = filteredAndSortedOrganizations.find(o => o.id === currentOrganizationId);
			if (found) return found;
		}
		return filteredAndSortedOrganizations[0];
	}, [filteredAndSortedOrganizations, currentOrganizationId]);

	const isActiveItem = useCallback((organizationId) => {
		return !!currentOrganization && organizationId === currentOrganization.id;
	}, [currentOrganization]);

	const initDeleteOrganization = useCallback((e, organizationId) => {
		e.stopPropagation();
		setDeleteTargetId(organizationId);
	}, []);

	const submitDeleteOrganization = useCallback((organizationId) => {
		setIsDeleting(true);
		deleteOrganization(organizationId)
			.then(() => {
				dispatch(storeDeleteOrganization(organizationId));
				setCurrentOrganizationId((prev) => (prev === organizationId ? null : prev));
			})
			.catch((e) => {
				console.error(e);
				Notification('An error occurred. The organization could not be deleted!', 'error');
			})
			.finally(() => {
				setIsDeleting(false);
				setDeleteTargetId(null);
			});
	}, [dispatch]);

	useEffect(() => {
		if (!user?.uid) return;

		let cancelled = false;
		setIsLoading(true);
		setLoadError(null);
		dispatch(storeClearOrganizations());

		getOrganizations(user.uid)
			.then((data) => {
				if (cancelled) return;
				if (data) {
					data.forEach((organization) => {
						dispatch(storeAddOrganization(organization));
					});
				}
			})
			.catch((e) => {
				if (cancelled) return;
				console.error(e);
				setLoadError('Could not load organizations. Please try again.');
			})
			.finally(() => {
				if (!cancelled) setIsLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [user?.uid, dispatch]);

	return (
		<div>
			<div className='animate__animated animate__fadeIn'>
				<div className="mtop--20 flex flex--row">
					<div className="profile__sidebar profile__sidebar-accounts">
						<AddOrganization user={user} />
						<hr />

						{showOrgsSearch && (
							<div style={{ position: 'relative' }}>
								<label htmlFor="organization-search" className="sr-only">Search organizations</label>
								<input
									id="organization-search"
									value={searchTerm}
									onChange={(e) => setSearchTerm(e.target.value)}
									type="text"
									placeholder="search"
								/>
							</div>
						)}

						{isLoading && (
							<div className="profile__sidebar-loading">Loading organizations…</div>
						)}

						{!isLoading && loadError && (
							<div className="profile__sidebar-error text--danger">{loadError}</div>
						)}

						{!isLoading && !loadError && filteredAndSortedOrganizations.length === 0 && (
							<div className="profile__sidebar-empty">
								{searchTerm ? 'No organizations match your search.' : 'No organizations yet.'}
							</div>
						)}

						{!isLoading && !loadError && filteredAndSortedOrganizations.map((organization) => (
							<div key={organization.id}>
								<div
									className={`profile__sidebar-item ${isActiveItem(organization.id) ? 'profile__sidebar-itemActive' : ''}`}
									onClick={() => setCurrentOrganizationId(organization.id)}
									role="button"
									tabIndex={0}
									aria-pressed={isActiveItem(organization.id)}
									onKeyDown={(e) => {
										if (e.key === 'Enter' || e.key === ' ') {
											e.preventDefault();
											setCurrentOrganizationId(organization.id);
										}
									}}
								>
									<span className="material-icons font--20 vm--align mright--10" aria-hidden="true"> business </span>
									{organization?.data?.name}
									<div
										type="button"
										className="profile__sidebar-itemDelete"
										aria-label={`Delete ${organization?.data?.name}`}
										onClick={(e) => initDeleteOrganization(e, organization.id)}
									>
										<span className="material-icons text--danger" aria-hidden="true">clear</span>
									</div>
								</div>
							</div>
						))}
					</div>

					<div className="profile__content">
						{currentOrganization && (
							<Organization
								organization={currentOrganization}
								setCurrentOrganization={(o) => setCurrentOrganizationId(o.id)}
							/>
						)}
					</div>
				</div>
			</div>

			{deleteTargetId && (
				<ConfirmDelete
					closeCb={() => setDeleteTargetId(null)}
					confirmCb={submitDeleteOrganization}
					confirmData={deleteTargetId}
					isSubmitting={isDeleting}
					text={'Are you sure you want to delete this organization? The organization should have no campaigns associated with it!'}
				/>
			)}
		</div>
	)
}

export default Organizations